import React, { useEffect, useRef, useState } from 'react';
import { Formik, FormikProps } from 'formik';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  DialogContent,
  FormControlLabel,
  Grid,
  IconButton,
  MenuItem,
  Radio,
  RadioGroup,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogActions from '@mui/material/DialogActions';
import CloseIcon from '@mui/icons-material/Close';
import { LoadingButton } from '@mui/lab';
import * as yup from 'yup';
import { useTranslation } from 'react-i18next';
import { usePostHog } from 'posthog-js/react';
import useSWR from 'swr';
import {
  AccountData,
  ExtraPaymentMode,
  ExtraPaymentResult,
  OrderData,
  OrderService,
  PaymentIntegrationType,
  StripeReaderData,
  StripeReaderPaymentData,
  StripeReaderPaymentStatus,
  StripeService,
} from '../../../../../api';
import { ApiError } from '../../../../../api/core/ApiError';
import { ColorButton } from '../../../../../common/ui/ConfimationDialog';
import { useSafeExecute } from '../../../../../common/apiNotifications/safeExecute';
import { ApiResponseHandlerType } from '../../../../../common/apiNotifications/ApiResponseHandlerContext';
import { useApiResponseHandlerContext } from '../../../../../common/apiNotifications/useApiResponseHandlerContext';
import { bookingFeeAmount, formatMoney, isBookingFeeOn, withBookingFee } from '../../../../../common/bookingFee';
import { useIsGrowAccount } from '../../../../../common/payments/useIsGrowAccount';
import { usePaymentCapabilities } from '../../../../../common/payments/usePaymentCapabilities';
import { useGetAccountAuthenticated } from '../../../../../session/swr/useGetAccountAuthenticated';
import { useAuth } from '../../../../../session/InternalAuthProvider';
import { UseAuthPermissions } from '../../../../../session/UseAuthPermissions';
import { getLinkToPaymentLink } from '../../OrderDetails';
import { MonetaryVoucherPicker } from './MonetaryVoucherPicker';

const T_PREFIX = 'calendar.edit_order.order_details.extraPayment';
const POS_POLL_INTERVAL_MS = 3000;
const READER_POLL_INTERVAL_MS = 2000;
const READER_OFFLINE_ERROR_ID = 'terminal_reader_offline';
const READER_BUSY_ERROR_ID = 'terminal_reader_busy';
const AMOUNT_SX = { fontVariantNumeric: 'tabular-nums' };
const STICKY_ACTIONS_SX = { position: 'sticky', bottom: 0, bgcolor: 'background.paper' };
const PHONE_ACTIONS_SX = {
  ...STICKY_ACTIONS_SX,
  flexDirection: 'column',
  alignItems: 'stretch',
  gap: 1,
  p: 2,
  '& > :not(style) ~ :not(style)': { margin: 0 },
};

type ExtraPaymentFormValues = {
  title: string;
  // Deliberately allowed to start empty rather than at 0: a prefilled 0 passes
  // `required()` and lets a zero-value line through, which the charge path then rejects.
  price: number | '';
  mode: ExtraPaymentMode;
  paymentMethodId: string;
  voucherId: string;
  voucherBalance?: number;
  posDeviceId: string;
};

type PosPaymentInProgress = {
  url: string;
  lineId: string;
};

type ReaderPaymentInProgress = {
  lineId: string;
  readerLabel: string;
  amount: string;
  status: StripeReaderPaymentStatus;
};

type PendingReaderAction = 'cancel' | 'retry';

type ExtraPaymentDialogProps = {
  isOpen: boolean;
  onClose: () => void;
  orderId: string;
  order: OrderData;
  onCommitted: (freshOrder: OrderData) => void;
  // Prefilled when the dialog is opened to settle a known balance. Staff still confirm the
  // amount and pick how it is collected.
  initialPrice?: number;
  initialTitle?: string;
};

export const ExtraPaymentDialog = ({
  isOpen,
  onClose,
  orderId,
  order,
  onCommitted,
  initialPrice,
  initialTitle,
}: ExtraPaymentDialogProps) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isFullScreen = useMediaQuery(theme.breakpoints.down('md'));
  const isPhone = useMediaQuery(theme.breakpoints.down('sm'));
  const posthog = usePostHog();
  const safeExecute = useSafeExecute();
  const { showResponseNotification } = useApiResponseHandlerContext();
  const formikRef = useRef<FormikProps<ExtraPaymentFormValues>>(null);
  const posWindowRef = useRef<Window | null>(null);
  const [posPayment, setPosPayment] = useState<PosPaymentInProgress | null>(null);
  const [readerPayment, setReaderPayment] = useState<ReaderPaymentInProgress | null>(null);
  const [pendingReaderAction, setPendingReaderAction] = useState<PendingReaderAction | null>(null);
  const [sendError, setSendError] = useState('');
  const isGrow = useIsGrowAccount();
  const { saved_cards: supportsSavedCards, card_readers: supportsCardReaders } = usePaymentCapabilities();
  const { isAdmin } = UseAuthPermissions();
  const { authState } = useAuth();
  const account = authState.account as unknown as AccountData;
  const isStripe =
    account?.payment_integration_settings?.payment_integration_type === PaymentIntegrationType.STRIPE_CONNECT;
  const { data: readersData, isLoading: isLoadingReaders } = useSWR(
    isOpen && isStripe && supportsCardReaders ? ['stripeReaders'] : null,
    () => StripeService.getStripeReaders(),
    { revalidateOnFocus: false },
  );
  const readers: StripeReaderData[] = readersData ?? [];
  const onlineReaders = readers.filter((reader) => reader.online);
  const { account: settingsAccount } = useGetAccountAuthenticated({});
  const paymentMethods = settingsAccount?.other_payment_methods ?? [];
  const posDevices = account?.payment_integration_settings?.pos_devices ?? [];

  const hasSavedCard = !!order?.has_saved_card;
  const cardSuffix = order?.card_suffix;

  // One reason only, most actionable first.
  const savedCardDisabledReason = !isAdmin
    ? t(`${T_PREFIX}.modeSavedCardDisabledNotAdmin`)
    : !supportsSavedCards
    ? t(`${T_PREFIX}.modeSavedCardDisabledNoIntegration`)
    : !hasSavedCard
    ? t(`${T_PREFIX}.modeSavedCardDisabledNoCard`)
    : '';

  const growPosDisabledReason = !isGrow
    ? t(`${T_PREFIX}.modePosDisabledNoGrow`)
    : !posDevices.length
    ? t(`${T_PREFIX}.modePosDisabledNoDevice`)
    : '';
  const stripePosDisabledReason = onlineReaders.length ? '' : t(`${T_PREFIX}.reader.disabledNoReader`);
  const posDisabledReason = isStripe ? stripePosDisabledReason : growPosDisabledReason;

  const paidWithMonetaryVoucher = !!order?.voucher_id && !!order?.voucher_amount;
  const initialMode = paidWithMonetaryVoucher
    ? ExtraPaymentMode.VOUCHER
    : !posDisabledReason
    ? ExtraPaymentMode.POS_TERMINAL
    : !savedCardDisabledReason
    ? ExtraPaymentMode.SAVED_CARD_CHARGE
    : ExtraPaymentMode.RECORD_ONLY;

  const hasPaymentIntegration =
    !!account?.payment_integration_settings &&
    account.payment_integration_settings.payment_integration_type !== PaymentIntegrationType.NO_INTEGRATION;

  const paymentLinkDisabledReason = !hasPaymentIntegration ? t(`${T_PREFIX}.modePaymentLinkDisabledNoIntegration`) : '';

  const isBookingFeeActive = isBookingFeeOn(account);

  const savedCardHint = cardSuffix
    ? t(`${T_PREFIX}.modeSavedCardHintSuffix`, { suffix: cardSuffix })
    : t(`${T_PREFIX}.modeSavedCardHint`);
  const immediateChargeWarning = cardSuffix
    ? t(`${T_PREFIX}.immediateChargeWarningSuffix`, { suffix: cardSuffix })
    : t(`${T_PREFIX}.immediateChargeWarning`);

  const validationSchema = yup.object().shape({
    title: yup.string().trim().required(t('calendar.edit_order.mandatoryField')),
    price: yup
      .number()
      .typeError(t('calendar.edit_order.mandatoryField'))
      .moreThan(0, t('calendar.edit_order.positive'))
      .required(t('calendar.edit_order.mandatoryField')),
    mode: yup.mixed<ExtraPaymentMode>().oneOf(Object.values(ExtraPaymentMode)).required(),
    paymentMethodId: yup.string().when('mode', {
      is: ExtraPaymentMode.RECORD_ONLY,
      then: (schema) => (paymentMethods.length ? schema.required(t('calendar.edit_order.mandatoryField')) : schema),
    }),
    voucherId: yup.string().when('mode', {
      is: ExtraPaymentMode.VOUCHER,
      then: (schema) => schema.required(t('calendar.edit_order.mandatoryField')),
    }),
    posDeviceId: yup.string().when('mode', {
      is: ExtraPaymentMode.POS_TERMINAL,
      then: (schema) => schema.required(t('calendar.edit_order.mandatoryField')),
    }),
    voucherBalance: yup.number().when(['mode', 'price'], {
      is: (mode: ExtraPaymentMode, price: number) => mode === ExtraPaymentMode.VOUCHER && price > 0,
      then: (schema) =>
        schema.test(
          'covers-price',
          t(`${T_PREFIX}.errors.voucherBalanceTooLow`),
          (balance, context) => balance === undefined || balance >= context.parent.price,
        ),
    }),
  });

  const initialValues: ExtraPaymentFormValues = {
    title: initialTitle ?? '',
    price: initialPrice ?? '',
    mode: initialMode,
    paymentMethodId:
      paymentMethods.find((method) => method.id === settingsAccount?.default_other_payment_method_id)?.id ??
      paymentMethods[0]?.id ??
      '',
    voucherId: '',
    posDeviceId: (isStripe ? onlineReaders[0]?.id : posDevices[0]?.id) ?? '',
  };

  const closeDialog = () => {
    setPosPayment(null);
    setReaderPayment(null);
    setSendError('');
    formikRef.current?.resetForm();
    onClose();
  };

  useEffect(() => {
    if (!posPayment) return undefined;
    let cancelled = false;
    let inFlight = false;
    const interval = setInterval(() => {
      if (inFlight) return;
      inFlight = true;
      OrderService.getOrder(orderId)
        .then((freshOrder) => {
          if (cancelled || !freshOrder.extra_payment_lines.some((line) => line.id === posPayment.lineId)) return;
          showResponseNotification(t(`${T_PREFIX}.successPosPaid`), ApiResponseHandlerType.SUCCESS);
          onCommitted(freshOrder);
          closeDialog();
        })
        .catch(() => {})
        .finally(() => {
          inFlight = false;
        });
    }, POS_POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [posPayment]);

  const stopWaitingForPosPayment = () => {
    showResponseNotification(t(`${T_PREFIX}.posWaitingStopped`), ApiResponseHandlerType.WARNING);
    closeDialog();
    OrderService.getOrder(orderId)
      .then(onCommitted)
      .catch(() => {});
  };

  const successMessageFor = (mode: ExtraPaymentMode) => {
    if (mode === ExtraPaymentMode.PAYMENT_LINK) return t(`${T_PREFIX}.successPaymentLink`);
    if (mode === ExtraPaymentMode.SAVED_CARD_CHARGE) return t(`${T_PREFIX}.successCharged`);
    if (mode === ExtraPaymentMode.VOUCHER) return t(`${T_PREFIX}.successVoucher`);
    return t(`${T_PREFIX}.successRecordOnly`);
  };

  // FastAPI serialises HTTPException(detail=...) as {"detail": ...}, so safeExecute's default
  // (error.body?.message) never matches and always falls through to the generic string.
  const mapExtraPaymentError = (error: ApiError, readerLabel = ''): string => {
    const detail = error.body?.detail;

    if (detail === 'order has no saved card') {
      return t(`${T_PREFIX}.errorNoSavedCard`);
    }
    if (detail === 'voucher balance is too low') {
      return t(`${T_PREFIX}.errors.voucherBalanceTooLow`);
    }
    if (detail === 'voucher not found') {
      return t(`${T_PREFIX}.errors.voucherNotFound`);
    }
    if (detail === 'Voucher is too old') {
      return t(`${T_PREFIX}.errors.voucherTooOld`);
    }
    if (detail === 'voucher redemption limit reached') {
      return t(`${T_PREFIX}.errors.voucherRedemptionLimit`);
    }
    if (detail?.provider_error?.id === READER_OFFLINE_ERROR_ID) {
      return t(`${T_PREFIX}.reader.offlineError`, { reader: readerLabel });
    }
    if (detail?.provider_error?.id === READER_BUSY_ERROR_ID) {
      return t(`${T_PREFIX}.reader.busyError`, { reader: readerLabel });
    }
    if (detail && typeof detail === 'object' && (detail.provider_error ?? detail.grow_error)) {
      const providerError = detail.provider_error ?? detail.grow_error;
      return t(`${T_PREFIX}.errorProviderRejected`, {
        message: providerError.message || providerError.id || '',
      });
    }
    if (typeof detail === 'string') {
      return detail;
    }
    return t(`${T_PREFIX}.errorGeneric`);
  };

  const openPosPage = (url: string) => {
    const posWindow = posWindowRef.current;
    posWindowRef.current = null;
    if (!posWindow) {
      window.open(url, '_blank', 'noopener');
      return;
    }
    posWindow.opener = null;
    posWindow.location.href = url;
  };

  const finishReaderPayment = async () => {
    const freshOrder = await OrderService.getOrder(orderId);
    showResponseNotification(t(`${T_PREFIX}.reader.paid`), ApiResponseHandlerType.SUCCESS);
    onCommitted(freshOrder);
    closeDialog();
  };

  const applyReaderStatus = async (data: StripeReaderPaymentData) => {
    if (data.status === StripeReaderPaymentStatus.PAID) {
      await finishReaderPayment();
      return;
    }
    setReaderPayment((current) => (current?.lineId === data.line_id ? { ...current, status: data.status } : current));
  };

  useEffect(() => {
    if (readerPayment?.status !== StripeReaderPaymentStatus.WAITING) return undefined;
    const { lineId } = readerPayment;
    let cancelled = false;
    let inFlight = false;
    const interval = setInterval(() => {
      if (inFlight) return;
      inFlight = true;
      StripeService.getStripeReaderPayment(orderId, lineId)
        .then((data) => {
          if (cancelled) return undefined;
          return applyReaderStatus(data);
        })
        .catch(() => {})
        .finally(() => {
          inFlight = false;
        });
    }, READER_POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [readerPayment?.lineId, readerPayment?.status]);

  const cancelReaderPayment = async () => {
    if (!readerPayment) return;
    setPendingReaderAction('cancel');
    try {
      const data = await safeExecute({
        apiCall: () => StripeService.cancelStripeReaderPayment(orderId, readerPayment.lineId),
        successMessage: '',
        suppressSuccessMessage: true,
        onErrorMessageGenerator: (error) => mapExtraPaymentError(error, readerPayment.readerLabel),
      });
      if (data.status === StripeReaderPaymentStatus.CANCELED) {
        closeDialog();
        return;
      }
      await applyReaderStatus(data);
    } catch {
      // safeExecute already surfaced the error; the dialog stays open.
    } finally {
      setPendingReaderAction(null);
    }
  };

  const retryReaderPayment = async () => {
    if (!readerPayment) return;
    setPendingReaderAction('retry');
    try {
      const data = await safeExecute({
        apiCall: () => StripeService.retryStripeReaderPayment(orderId, readerPayment.lineId),
        successMessage: '',
        suppressSuccessMessage: true,
        onErrorMessageGenerator: (error) => mapExtraPaymentError(error, readerPayment.readerLabel),
      });
      await applyReaderStatus(data);
    } catch {
      // safeExecute already surfaced the error; the dialog stays open.
    } finally {
      setPendingReaderAction(null);
    }
  };

  const sendToReader = async (apiCall: () => Promise<ExtraPaymentResult>, readerLabel: string) => {
    try {
      return await apiCall();
    } catch (error) {
      setSendError(
        error instanceof ApiError ? mapExtraPaymentError(error, readerLabel) : t(`${T_PREFIX}.errorGeneric`),
      );
      throw error;
    }
  };

  const commit = async (values: ExtraPaymentFormValues, resetForm: () => void) => {
    const isReaderSend = isStripe && values.mode === ExtraPaymentMode.POS_TERMINAL;
    const selectedReader = readers.find((reader) => reader.id === values.posDeviceId);
    const sendPayment = () =>
      OrderService.addOrderExtraPayment({
        order_id: orderId,
        price: Number(values.price),
        title: values.title.trim(),
        mode: values.mode,
        payment_method_id:
          values.mode === ExtraPaymentMode.RECORD_ONLY && values.paymentMethodId ? values.paymentMethodId : undefined,
        voucher_id: values.mode === ExtraPaymentMode.VOUCHER ? values.voucherId : undefined,
        pos_device_id: values.mode === ExtraPaymentMode.POS_TERMINAL ? values.posDeviceId : undefined,
      });

    setSendError('');
    const result = isReaderSend
      ? await sendToReader(sendPayment, selectedReader?.label ?? '')
      : await safeExecute({
          apiCall: sendPayment,
          successMessage: successMessageFor(values.mode),
          suppressSuccessMessage: values.mode === ExtraPaymentMode.POS_TERMINAL,
          onErrorMessageGenerator: mapExtraPaymentError,
        });

    if (result.payment_link) {
      void navigator.clipboard.writeText(getLinkToPaymentLink(result.payment_link.id));
    }

    posthog.capture('Order Extra Payment Added', {
      mode: values.mode,
      price: Number(values.price),
      order_id: orderId,
    });

    if (isReaderSend && result.extra_payment_line_id) {
      setReaderPayment({
        lineId: result.extra_payment_line_id,
        readerLabel: selectedReader?.label ?? '',
        amount: formatMoney(withBookingFee(Number(values.price), account), account),
        status: StripeReaderPaymentStatus.WAITING,
      });
      return;
    }

    if (result.pos_url && result.extra_payment_line_id) {
      openPosPage(result.pos_url);
      setPosPayment({ url: result.pos_url, lineId: result.extra_payment_line_id });
      return;
    }

    // The endpoint returns a result, not the order, so refetch to get the authoritative
    // extra payment lines and has_extra_payment_links flag.
    const freshOrder = await OrderService.getOrder(orderId);
    onCommitted(freshOrder);
    resetForm();
    onClose();
  };

  // Browsers block a popup opened after an await, so the Grow tab is opened empty inside the click
  // and pointed at the Grow url once the server returns it. Opening that url sends the charge to the device.
  const confirm = async (mode: ExtraPaymentMode, submitForm: () => Promise<void>) => {
    if (mode === ExtraPaymentMode.POS_TERMINAL && !isStripe) {
      posWindowRef.current = window.open('', '_blank');
    }
    try {
      await submitForm();
    } finally {
      posWindowRef.current?.close();
      posWindowRef.current = null;
    }
  };

  const onSubmit = async (values: ExtraPaymentFormValues, { resetForm }: { resetForm: () => void }) => {
    // safeExecute already surfaced the error toast; swallow so the dialog stays open with
    // the user's input intact.
    await commit(values, resetForm).catch(() => {});
  };

  const modeRow = (
    mode: ExtraPaymentMode,
    selectedMode: ExtraPaymentMode,
    labelKey: string,
    hint: string,
    disabledReason: string,
  ) => (
    <Tooltip title={disabledReason} arrow disableHoverListener={!disabledReason}>
      {/* A disabled control swallows pointer events, so the tooltip needs a live wrapper. */}
      <span>
        <FormControlLabel
          value={mode}
          disabled={!!disabledReason}
          sx={{ alignItems: 'flex-start', mb: 0.5 }}
          control={<Radio size="small" sx={{ pt: 0.25 }} />}
          label={
            <Grid container flexDirection="column">
              <Typography variant="body2">{t(labelKey)}</Typography>
              {(disabledReason || !isPhone || mode === selectedMode) && (
                <Typography variant="caption" color="text.secondary">
                  {disabledReason || hint}
                </Typography>
              )}
            </Grid>
          }
        />
      </span>
    </Tooltip>
  );

  const readerStatusChip = (reader: StripeReaderData) => (
    <Chip
      size="small"
      variant="outlined"
      color={reader.online ? 'success' : 'default'}
      label={t(`${T_PREFIX}.reader.${reader.online ? 'online' : 'offline'}`)}
    />
  );

  return (
    <Formik
      innerRef={formikRef}
      enableReinitialize
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={onSubmit}>
      {({ values, errors, touched, handleChange, setFieldValue, submitForm, isSubmitting }) => {
        const price = Number(values.price);
        const isReaderMode = isStripe && values.mode === ExtraPaymentMode.POS_TERMINAL;
        const isFeeCharge =
          (values.mode === ExtraPaymentMode.SAVED_CARD_CHARGE || isReaderMode) && isBookingFeeActive && price > 0;
        const feeAmount = bookingFeeAmount(price, account);
        const chargedTotal = withBookingFee(price, account);
        const confirmLabel =
          isReaderMode && price > 0
            ? t(`${T_PREFIX}.reader.send`, { amount: formatMoney(chargedTotal, account) })
            : isFeeCharge
            ? t(`${T_PREFIX}.chargeAmount`, { amount: formatMoney(chargedTotal, account) })
            : t(`${T_PREFIX}.confirm`);
        const phoneButtonProps = isPhone ? { fullWidth: true } : {};
        const renderActions = (secondaryAction: React.ReactNode, mainAction: React.ReactNode) => (
          <DialogActions sx={isPhone ? PHONE_ACTIONS_SX : STICKY_ACTIONS_SX}>
            {isPhone ? (
              <>
                {mainAction}
                {secondaryAction}
              </>
            ) : (
              <>
                {secondaryAction}
                {mainAction}
              </>
            )}
          </DialogActions>
        );
        const dialogTitle = t('calendar.edit_order.order_details.extraPaymentItem');
        return (
          <Dialog open={isOpen} fullScreen={isFullScreen} onClose={posPayment ? stopWaitingForPosPayment : closeDialog}>
            {isPhone ? (
              <Grid
                container
                alignItems="center"
                flexWrap="nowrap"
                gap={1}
                px={1}
                py={1}
                borderBottom="1px solid"
                borderColor="divider">
                <IconButton aria-label={t('close')} onClick={posPayment ? stopWaitingForPosPayment : closeDialog}>
                  <CloseIcon />
                </IconButton>
                <Typography variant="h6" component="h2" id="alert-dialog-title">
                  {dialogTitle}
                </Typography>
              </Grid>
            ) : (
              <DialogTitle id="alert-dialog-title">{dialogTitle}</DialogTitle>
            )}
            {posPayment ? (
              <>
                <DialogContent>
                  <Grid container flexDirection="column" gap={2}>
                    <Alert severity="info">{t(`${T_PREFIX}.posWaiting`)}</Alert>
                    <Button href={posPayment.url} target="_blank" rel="noopener">
                      {t(`${T_PREFIX}.posReopen`)}
                    </Button>
                  </Grid>
                </DialogContent>
                {renderActions(
                  null,
                  <ColorButton onClick={stopWaitingForPosPayment} {...phoneButtonProps}>
                    {t(`${T_PREFIX}.posStopWaiting`)}
                  </ColorButton>,
                )}
              </>
            ) : readerPayment ? (
              <>
                <DialogContent
                  sx={isPhone ? { display: 'flex', flexDirection: 'column', justifyContent: 'center' } : {}}>
                  <Grid container flexDirection="column" alignItems="center" textAlign="center" gap={1} py={2}>
                    {readerPayment.status === StripeReaderPaymentStatus.WAITING && <CircularProgress aria-hidden />}
                    <Typography variant="h4" fontWeight={700} dir="ltr" sx={AMOUNT_SX}>
                      {readerPayment.amount}
                    </Typography>
                    {readerPayment.status === StripeReaderPaymentStatus.WAITING ? (
                      <>
                        <Typography variant="subtitle1" role="status">
                          {t(`${T_PREFIX}.reader.waitingTitle`, { reader: readerPayment.readerLabel })}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {t(`${T_PREFIX}.reader.waitingBody`)}
                        </Typography>
                      </>
                    ) : (
                      <>
                        <Typography variant="body2" color="text.secondary">
                          {readerPayment.readerLabel}
                        </Typography>
                        <Alert
                          severity="error"
                          sx={{ width: '100%', boxSizing: 'border-box', textAlign: 'start', mt: 1 }}>
                          {readerPayment.status === StripeReaderPaymentStatus.DECLINED
                            ? t(`${T_PREFIX}.reader.declined`)
                            : t(`${T_PREFIX}.reader.customerCanceled`)}
                        </Alert>
                      </>
                    )}
                  </Grid>
                </DialogContent>
                {readerPayment.status === StripeReaderPaymentStatus.WAITING
                  ? renderActions(
                      null,
                      <LoadingButton
                        variant="outlined"
                        color="error"
                        size={isPhone ? 'large' : 'medium'}
                        loading={pendingReaderAction === 'cancel'}
                        onClick={cancelReaderPayment}
                        {...phoneButtonProps}>
                        {t(`${T_PREFIX}.reader.cancel`)}
                      </LoadingButton>,
                    )
                  : renderActions(
                      <LoadingButton
                        variant="outlined"
                        color="inherit"
                        loading={pendingReaderAction === 'cancel'}
                        disabled={!!pendingReaderAction}
                        onClick={cancelReaderPayment}
                        {...phoneButtonProps}>
                        {t(`${T_PREFIX}.reader.close`)}
                      </LoadingButton>,
                      <LoadingButton
                        variant="contained"
                        size={isPhone ? 'large' : 'medium'}
                        loading={pendingReaderAction === 'retry'}
                        disabled={!!pendingReaderAction}
                        onClick={retryReaderPayment}
                        sx={{ boxShadow: 'none' }}
                        {...phoneButtonProps}>
                        {t(`${T_PREFIX}.reader.tryAgain`)}
                      </LoadingButton>,
                    )}
              </>
            ) : isLoadingReaders ? (
              <DialogContent sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                <CircularProgress />
              </DialogContent>
            ) : (
              <>
                <DialogContent>
                  <Grid container flexDirection="column" flexWrap="nowrap" gap={2}>
                    <TextField
                      size="small"
                      id="title"
                      name="title"
                      variant="outlined"
                      label={t('calendar.edit_order.description')}
                      value={values.title}
                      onChange={handleChange}
                      error={touched.title && Boolean(errors.title)}
                      helperText={touched.title && errors.title}
                    />
                    <TextField
                      size="small"
                      variant="outlined"
                      type="number"
                      id="price"
                      name="price"
                      label={t('calendar.edit_order.priceWord')}
                      value={values.price}
                      onChange={handleChange}
                      error={touched.price && Boolean(errors.price)}
                      helperText={touched.price && errors.price}
                    />
                    <Grid
                      container
                      flexDirection="column"
                      gap={0.5}
                      borderRadius="8px"
                      border="1px solid"
                      borderColor="divider"
                      p={1.5}>
                      <Typography fontWeight={700}>{t(`${T_PREFIX}.modeLabel`)}</Typography>
                      <RadioGroup
                        value={values.mode}
                        onChange={(e) => {
                          setSendError('');
                          void setFieldValue('mode', e.target.value as ExtraPaymentMode);
                        }}>
                        {modeRow(
                          ExtraPaymentMode.POS_TERMINAL,
                          values.mode,
                          isStripe ? `${T_PREFIX}.reader.mode` : `${T_PREFIX}.modes.posTerminal`,
                          isStripe ? t(`${T_PREFIX}.reader.hint`) : t(`${T_PREFIX}.modePosTerminalHint`),
                          posDisabledReason,
                        )}
                        {values.mode === ExtraPaymentMode.POS_TERMINAL && isStripe && (
                          <TextField
                            select
                            size="small"
                            label={t(`${T_PREFIX}.reader.label`)}
                            value={values.posDeviceId}
                            onChange={(event) => setFieldValue('posDeviceId', event.target.value)}
                            error={touched.posDeviceId && Boolean(errors.posDeviceId)}
                            helperText={touched.posDeviceId && errors.posDeviceId}
                            SelectProps={{
                              renderValue: (selectedId) => {
                                const selectedReader = readers.find((reader) => reader.id === selectedId);
                                return selectedReader ? (
                                  <Box display="flex" alignItems="center" justifyContent="space-between" gap={1}>
                                    <span>{selectedReader.label}</span>
                                    {readerStatusChip(selectedReader)}
                                  </Box>
                                ) : (
                                  ''
                                );
                              },
                            }}
                            sx={{ marginInlineStart: 4, maxWidth: isPhone ? 'none' : 320, mb: 1 }}>
                            {readers.map((reader) => (
                              <MenuItem key={reader.id} value={reader.id} disabled={!reader.online}>
                                <Box
                                  display="flex"
                                  alignItems="center"
                                  justifyContent="space-between"
                                  gap={2}
                                  width="100%">
                                  <Box display="flex" flexDirection="column">
                                    <span>{reader.label}</span>
                                    <Typography variant="caption" color="text.secondary">
                                      {reader.device_type}
                                    </Typography>
                                  </Box>
                                  {readerStatusChip(reader)}
                                </Box>
                              </MenuItem>
                            ))}
                          </TextField>
                        )}
                        {values.mode === ExtraPaymentMode.POS_TERMINAL && !isStripe && posDevices.length > 1 && (
                          <TextField
                            select
                            size="small"
                            label={t(`${T_PREFIX}.posDevice`)}
                            value={values.posDeviceId}
                            onChange={(event) => setFieldValue('posDeviceId', event.target.value)}
                            error={touched.posDeviceId && Boolean(errors.posDeviceId)}
                            helperText={touched.posDeviceId && errors.posDeviceId}
                            sx={{ marginInlineStart: 4, maxWidth: 260, mb: 1 }}>
                            {posDevices.map((device) => (
                              <MenuItem key={device.id} value={device.id}>
                                {device.label}
                              </MenuItem>
                            ))}
                          </TextField>
                        )}
                        {modeRow(
                          ExtraPaymentMode.SAVED_CARD_CHARGE,
                          values.mode,
                          `${T_PREFIX}.modeSavedCard`,
                          savedCardHint,
                          savedCardDisabledReason,
                        )}
                        {modeRow(
                          ExtraPaymentMode.RECORD_ONLY,
                          values.mode,
                          `${T_PREFIX}.modeRecordOnly`,
                          isBookingFeeActive
                            ? `${t(`${T_PREFIX}.modeRecordOnlyHint`)} · ${t(`${T_PREFIX}.noBookingFee`)}`
                            : t(`${T_PREFIX}.modeRecordOnlyHint`),
                          '',
                        )}
                        {values.mode === ExtraPaymentMode.RECORD_ONLY && paymentMethods.length > 0 && (
                          <TextField
                            select
                            size="small"
                            label={t(`${T_PREFIX}.paymentMethod`)}
                            value={values.paymentMethodId}
                            onChange={(event) => setFieldValue('paymentMethodId', event.target.value)}
                            error={touched.paymentMethodId && Boolean(errors.paymentMethodId)}
                            helperText={touched.paymentMethodId && errors.paymentMethodId}
                            sx={{ marginInlineStart: 4, maxWidth: 260, mb: 1 }}>
                            {paymentMethods.map((method) => (
                              <MenuItem key={method.id} value={method.id}>
                                {method.label}
                              </MenuItem>
                            ))}
                          </TextField>
                        )}
                        {modeRow(
                          ExtraPaymentMode.PAYMENT_LINK,
                          values.mode,
                          `${T_PREFIX}.modePaymentLink`,
                          t(`${T_PREFIX}.modePaymentLinkHint`),
                          paymentLinkDisabledReason,
                        )}
                        {modeRow(
                          ExtraPaymentMode.VOUCHER,
                          values.mode,
                          `${T_PREFIX}.modes.voucher`,
                          t(`${T_PREFIX}.modeVoucherHint`),
                          '',
                        )}
                        {values.mode === ExtraPaymentMode.VOUCHER && (
                          <MonetaryVoucherPicker orderVoucherId={order?.voucher_id} />
                        )}
                      </RadioGroup>
                      {/* There is no confirmation step before the charge, so this is the last
                    thing standing between the admin and a real card being debited. */}
                      {isFeeCharge && (
                        <Grid
                          container
                          flexDirection="column"
                          flexWrap="nowrap"
                          gap={0.5}
                          p={1.5}
                          mt={1}
                          borderRadius="6px"
                          sx={{ bgcolor: 'mb.ground' }}>
                          <Grid container justifyContent="space-between" flexWrap="nowrap">
                            <Typography variant="body2">{t('calendar.edit_order.priceWord')}</Typography>
                            <Typography variant="body2" dir="ltr" sx={AMOUNT_SX}>
                              {formatMoney(price, account)}
                            </Typography>
                          </Grid>
                          <Grid container justifyContent="space-between" flexWrap="nowrap">
                            <Grid container item width="auto" alignItems="baseline" gap={0.5}>
                              <Typography variant="body2">{t(`${T_PREFIX}.bookingFee`)}</Typography>
                              <Typography variant="caption" color="text.secondary" dir="ltr">
                                {`(${account.booking_fee?.rate_percent}%)`}
                              </Typography>
                            </Grid>
                            <Typography variant="body2" dir="ltr" sx={AMOUNT_SX}>
                              {formatMoney(feeAmount, account)}
                            </Typography>
                          </Grid>
                          <Grid container justifyContent="space-between" flexWrap="nowrap">
                            <Typography variant="body2" fontWeight={700}>
                              {t(`${T_PREFIX}.customerCharged`)}
                            </Typography>
                            <Typography variant="body2" fontWeight={700} dir="ltr" sx={AMOUNT_SX}>
                              {formatMoney(chargedTotal, account)}
                            </Typography>
                          </Grid>
                        </Grid>
                      )}
                      {values.mode === ExtraPaymentMode.SAVED_CARD_CHARGE && !savedCardDisabledReason && (
                        <Alert severity="warning" sx={{ mt: 1 }}>
                          {isFeeCharge && cardSuffix
                            ? t(`${T_PREFIX}.immediateChargeAmount`, {
                                suffix: cardSuffix,
                                amount: formatMoney(chargedTotal, account),
                              })
                            : immediateChargeWarning}
                        </Alert>
                      )}
                    </Grid>
                    {sendError && <Alert severity="error">{sendError}</Alert>}
                  </Grid>
                </DialogContent>
                {renderActions(
                  <ColorButton onClick={closeDialog} {...phoneButtonProps}>
                    {t('cancel')}
                  </ColorButton>,
                  <LoadingButton
                    loading={isSubmitting}
                    variant="contained"
                    size={isPhone ? 'large' : 'medium'}
                    onClick={() => confirm(values.mode, submitForm)}
                    sx={{ boxShadow: 'none' }}
                    autoFocus
                    {...phoneButtonProps}>
                    {confirmLabel}
                  </LoadingButton>,
                )}
              </>
            )}
          </Dialog>
        );
      }}
    </Formik>
  );
};
